// eslint-disable-next-line no-unused-vars
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { extractErrorMessage, success, failure } from 'utils/errorHandling'

import useInnovaAuth from 'components/common/hooks/useInnovaAuth'

import { createMessageIdString, parseMsgStrToJson } from 'utils/chatFunctions'
import { buildPipeDelimitedString } from 'utils/arrayFunctions'
import useInnovaAxios from './useInnovaAxios'
import { checkFeature, checkPermission } from 'components/userPermissions'

const defaultChatRoom = { roomId: -1, members: [], contacts: [] }
const RECONNECT_BASE_DELAY = 1000
const RECONNECT_MAX_DELAY = 30_000

const useChat = () => {
  const _isMounted = useRef(false)
  const socketRef = useRef(null)
  const reconnectAttemptRef = useRef(0)
  const reconnectTimeoutRef = useRef(null)
  const connectChatWebsocketRef = useRef(null)
  const { user, userRole } = useInnovaAuth()

  const [isLoadingUnreadMessages, setIsLoadingUnreadMessages] = useState(false)
  const [isLoadingChatRooms, setIsLoadingChatRooms] = useState(false)
  const [isLoadingContacts, setIsLoadingContacts] = useState(false)
  const [isCreatingChatRoom, setCreatingChatRoom] = useState(false)
  const [isAddingUsers, setIsAddingUsers] = useState(false)
  const [isRemovingUser, setIsRemovingUser] = useState(false)
  const [isLoadingChatMessages, setIsLoadingChatMessages] = useState(false)
  const [isMarkingMessagesAsRead, setIsMarkingMessagesAsRead] = useState(false)

  const [chatMessages, setChatMessages] = useState([])
  const [ownMessage, setOwnMessage] = useState(null)
  const [unreadMessages, setUnreadMessages] = useState(0)
  const [selectedChatRoom, setSelectedChatRoom] = useState(defaultChatRoom)
  const [usersOnline, setUsersOnline] = useState([])
  const [refreshChatRoomsFlag, setRefreshChatRoomsFlag] = useState(0)
  const [isInitialized, setInitialized] = useState(false)
  const [chatCollapsed, setChatCollapsed] = useState(false)
  const [chatRooms, setChatRooms] = useState([])
  const [contacts, setContacts] = useState([])

  const getUnreadChatMsgsRequest = useInnovaAxios({ url: '/chat/getUnreadMessages' })
  const getChatRoomsRequest = useInnovaAxios({ url: '/chat/getChatRooms' })
  const getContactsRequest = useInnovaAxios({ url: '/chat/getContacts' })
  const createChatRoomRequest = useInnovaAxios({ url: '/chat/createChatRoom' })
  const addUserChatRoomRequest = useInnovaAxios({ url: '/chat/addUserToChatRoom' })
  const removeUserChatRoomRequest = useInnovaAxios({ url: '/chat/removeUserChatRoom' })
  const getChatMessagesRequest = useInnovaAxios({ url: '/chat/getMessages' })
  const markMessagesAsReadRequest = useInnovaAxios({ url: '/chat/markMessagesRead' })

  /* ============================================================
     State Incrementers
     ============================================================ */

  const setNewMessage = (message) => {
    setChatMessages((messages) => [...messages, message])
    setUnreadMessages((unread) => unread + 1)
  }

  const refreshChatRooms = useCallback(() => {
    setRefreshChatRoomsFlag((counter) => counter + 1)
  }, [])

  /* ============================================================
     useEffect Hooks
     ============================================================ */

  useEffect(() => {
    if (_isMounted.current && user && user.name?.length > 0) {
      connectChatWebsocket()
      fetchUnreadMessages()
    }
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    _isMounted.current = true
    if (!isInitialized && user && user.name?.length > 0) {
      connectChatWebsocket()
      fetchUnreadMessages()
    }

    return () => {
      _isMounted.current = false
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      const activeSocket = socketRef.current
      socketRef.current = null
      if (
        activeSocket &&
        (activeSocket.readyState === WebSocket.OPEN || activeSocket.readyState === WebSocket.CONNECTING)
      ) {
        activeSocket.close()
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (_isMounted.current && refreshChatRoomsFlag > 0) fetchChatRooms()
  }, [refreshChatRoomsFlag]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ============================================================
     WebSocket Connection and Reconnection Logic
     ============================================================ */

  const scheduleReconnect = () => {
    if (!_isMounted.current || reconnectTimeoutRef.current) return

    const delay = Math.min(RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttemptRef.current), RECONNECT_MAX_DELAY)

    reconnectAttemptRef.current += 1

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null

      if (!_isMounted.current) return

      connectChatWebsocketRef.current?.()
    }, delay)
  }

  const connectChatWebsocket = () => {
    if (!_isMounted.current) return
    if (!user?.name) return

    const activeSocket = socketRef.current

    if (
      activeSocket &&
      (activeSocket.readyState === WebSocket.OPEN || activeSocket.readyState === WebSocket.CONNECTING)
    ) {
      return
    }

    try {
      let url = `${process.env.REACT_APP_ICP_API}/chat/ws?userName=${user.name}`

      url = url.replace('https', 'wss')
      url = url.replace('http', 'ws')

      const newSocket = new WebSocket(url)

      socketRef.current = newSocket

      newSocket.onopen = () => {
        // Ignore an obsolete socket.
        if (socketRef.current !== newSocket) return

        reconnectAttemptRef.current = 0
        setInitialized(true)
      }

      newSocket.onmessage = (msg) => {
        if (socketRef.current !== newSocket) return

        const message = parseMsgStrToJson(msg.data)

        if (message === null) return

        if (message.messageTypeStr === 'chatMessage') {
          setNewMessage(message)
          refreshChatRooms()
        }

        if (message.messageTypeStr === 'usersOnlineList') {
          setUsersOnline(message.message.split('|'))
        }

        if (message.messageTypeStr === 'usersDisconnected') {
          setUsersOnline((currentUsers) => currentUsers.filter((currentUser) => currentUser !== message.message))
        }

        if (message.messageTypeStr === 'usersConnected') {
          setUsersOnline((currentUsers) => {
            if (currentUsers.includes(message.message)) {
              return currentUsers
            }

            return [...currentUsers, message.message]
          })
        }
      }

      newSocket.onerror = () => {
        // Don't log expected WebSocket connection failures here.
        // onclose handles reconnection.
      }

      newSocket.onclose = () => {
        // Ignore an old socket that has been replaced.
        if (socketRef.current !== newSocket) return

        socketRef.current = null

        if (!_isMounted.current) return

        setInitialized(false)
        scheduleReconnect()
      }
    } catch (error) {
      console.error('failed to create chat websocket', error)

      socketRef.current = null

      if (_isMounted.current) {
        setInitialized(false)
        scheduleReconnect()
      }
    }
  }

  connectChatWebsocketRef.current = connectChatWebsocket

  /* ============================================================
     Fetch Unread Messages
     ============================================================ */

  const fetchUnreadMessages = useCallback(async () => {
    if (!checkFeature(7, userRole?.roleAttributes?.featureId)) return failure('Feature not enabled')
    if (!checkPermission('canEdit', userRole.roleAttributes?.permissions)) return failure('Insufficient permissions')
    if (isLoadingUnreadMessages) return success('fetch unread messages already in progress')

    setIsLoadingUnreadMessages(true)
    try {
      const response = await getUnreadChatMsgsRequest()
      setIsLoadingUnreadMessages(false)
      if (_isMounted.current && response?.data) {
        if (response.data.hasOwnProperty('unreadMsgs')) {
          setUnreadMessages(response.data.unreadMsgs)
        }
      }

      if (response?.error) {
        if (response.error?.code !== 'ERR_CANCELED') return failure('failed to fetch unread messages')
      }

      return success('fetch unread messages successful')
    } finally {
      setIsLoadingUnreadMessages(false)
    }
  }, [userRole, isLoadingUnreadMessages]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ============================================================
     Send Message
     ============================================================ */

  const sendMessage = (msg) => {
    if (typeof msg !== 'object' || !msg.hasOwnProperty('username')) {
      return failure('invalid message')
    }

    const activeSocket = socketRef.current
    if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) {
      scheduleReconnect()
      return failure('socket not connected; reconnect scheduled')
    }

    msg.message = msg.message.replace("'", '&sq;')
    msg.message = msg.message.replace('\\', '\\\\')
    try {
      activeSocket.send(JSON.stringify(msg))
      return success('message sent')
    } catch (error) {
      console.error('failed to send chat message', error)
      scheduleReconnect()
      return failure('failed to send message; reconnect scheduled')
    }
  }

  /* ============================================================
     Fetch Chat Rooms
     ============================================================ */

  const fetchChatRooms = useCallback(async () => {
    if (isLoadingChatRooms) return success('fetch chat rooms already in progress')

    setIsLoadingChatRooms(true)

    try {
      const response = await getChatRoomsRequest()

      if (response?.error) {
        if (response.error?.code !== 'ERR_CANCELED')
          return failure(extractErrorMessage(response.error), 'failed to fetch chat rooms')
      }

      if (_isMounted.current && response?.data) {
        setChatRooms(response.data)
      }

      return success('fetch chat rooms successful')
    } finally {
      setIsLoadingChatRooms(false)
    }
  }, [getChatRoomsRequest, isLoadingChatRooms])

  /* ============================================================
     Fetch Contacts
     ============================================================ */

  const fetchContacts = useCallback(async () => {
    if (isLoadingContacts) return success('fetch contacts already in progress')

    setIsLoadingContacts(true)

    try {
      const response = await getContactsRequest()

      if (response?.error) {
        if (response.error?.code !== 'ERR_CANCELED')
          return failure(extractErrorMessage(response.error), 'failed to fetch contacts')
      }

      if (_isMounted.current && response?.data) {
        setContacts(response.data)
      }

      return success('fetch contacts successful')
    } finally {
      setIsLoadingContacts(false)
    }
  }, [getContactsRequest, isLoadingContacts]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ============================================================
     Create Chat Room
     ============================================================ */

  const createChatRoom = useCallback(
    async (members) => {
      if (!members || members.length < 1) return failure('invalid members list')
      if (isCreatingChatRoom) return success('create chat room already in progress')

      setCreatingChatRoom(true)

      const payload = {
        memberList: buildPipeDelimitedString(members),
      }

      try {
        const response = await createChatRoomRequest(payload)

        if (response?.error) {
          if (response.error?.code !== 'ERR_CANCELED') {
            return failure(extractErrorMessage(response.error, 'Failed to create chat room'))
          }
        }

        if (response?.data) {
          await fetchChatRooms()
          if (response.data.hasOwnProperty('roomId') && response.data.roomId > 0) {
            setSelectedChatRoom({ roomId: response.data.roomId, members: members })
          }
        }

        return success('chat room created', response?.data?.roomId ?? -1)
      } finally {
        setCreatingChatRoom(false)
      }
    },
    [createChatRoomRequest, fetchChatRooms, isCreatingChatRoom],
  ) // eslint-disable-line react-hooks/exhaustive-deps

  /* ============================================================
     Add Users to Chat Room
     ============================================================ */

  const addUsersToChatRoom = useCallback(
    async (chatRoomId, users) => {
      if (chatRoomId < 1 || !Array.isArray(users) || users.length < 1)
        return failure('invalid chat room id or users list')
      if (isAddingUsers) return success('add users already in progress')

      setIsAddingUsers(true)

      try {
        const response = await addUserChatRoomRequest({
          chatRoomId,
          userList: buildPipeDelimitedString(users),
        })
        if (response?.error) {
          console.error('error saving users to chatroom', response.error)
          return failure(extractErrorMessage(response.error, 'Failed to add users to chat room'))
        }

        await fetchChatRooms()
        return success('users added to chat room')
      } finally {
        setIsAddingUsers(false)
      }
    },
    [addUserChatRoomRequest, fetchChatRooms, isAddingUsers],
  ) // eslint-disable-line react-hooks/exhaustive-deps

  /* ============================================================
     Remove User from Chat Room
     ============================================================ */

  const removeUserFromChatRoom = useCallback(
    async (chatRoomId, userName) => {
      if (chatRoomId < 1 || !userName) return failure('invalid chat room id or user name')
      if (isRemovingUser) return success('remove user already in progress')

      setIsRemovingUser(true)

      try {
        const response = await removeUserChatRoomRequest({
          chatRoomId,
          userList: userName,
        })
        if (response?.error) {
          console.error(`error removing user ${userName} from chatroom`, response.error)
          return failure(extractErrorMessage(response.error, 'Failed to remove user from chat room'))
        }

        await fetchChatRooms()
        return success('user removed from chat room')
      } finally {
        setIsRemovingUser(false)
      }
    },
    [removeUserChatRoomRequest, fetchChatRooms, isRemovingUser],
  ) // eslint-disable-line react-hooks/exhaustive-deps

  /* ============================================================
     Fetch Chat Messages
     ============================================================ */

  const fetchChatMessages = useCallback(
    async (chatRoomId) => {
      if (chatRoomId < 1) return failure('invalid chat room id')

      if (isLoadingChatMessages) return success('fetch chat messages already in progress')

      setIsLoadingChatMessages(true)

      try {
        const response = await getChatMessagesRequest({ chatRoomId })

        if (response?.error) {
          if (response.error?.code !== 'ERR_CANCELED') console.error('error fetching chat messages', response.error)
          return failure(extractErrorMessage(response.error, 'Failed to fetch chat messages'))
        }

        return success('chat messages fetched', Array.isArray(response?.data) ? response.data : [])
      } finally {
        setIsLoadingChatMessages(false)
      }
    },
    [getChatMessagesRequest, isLoadingChatMessages],
  ) // eslint-disable-line react-hooks/exhaustive-deps

  /* ============================================================
     Mark Chat Messages As Read
     ============================================================ */

  const markChatMessagesAsRead = useCallback(
    async (messages) => {
      if (!Array.isArray(messages) || messages.length < 1) return true
      if (isMarkingMessagesAsRead) return true

      setIsMarkingMessagesAsRead(true)

      try {
        const response = await markMessagesAsReadRequest({
          userList: user.name,
          messageList: createMessageIdString(messages),
        })
        if (response?.error) {
          if (response.error?.code !== 'ERR_CANCELED')
            return failure(extractErrorMessage(response.error, 'Failed to mark chat messages as read'))
        }

        await fetchUnreadMessages()
        refreshChatRooms()

        return success('chat messages marked as read')
      } finally {
        setIsMarkingMessagesAsRead(false)
      }
    },
    [markMessagesAsReadRequest, refreshChatRooms, isMarkingMessagesAsRead, user.name, fetchUnreadMessages],
  ) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    chatMessages,
    ownMessage,
    setOwnMessage,
    unreadMessages,
    selectedChatRoom,
    refreshChatRoomsFlag,
    fetchUnreadMessages,
    setSelectedChatRoom,
    sendMessage,
    refreshChatRooms,
    chatCollapsed,
    setChatCollapsed,
    chatRooms,
    contacts,
    fetchContacts,
    createChatRoom,
    addUsersToChatRoom,
    removeUserFromChatRoom,
    fetchChatMessages,
    markChatMessagesAsRead,
    fetchChatRooms,
    usersOnline,
  }
}

export default useChat
